/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { Injectable } from '@angular/core';
import { TierModel } from '../../models';
import { environment } from '../../../../../../environments/environment';

@Injectable()
export class PortalRbacService {
    /**
     * Selected Tier, NOTE: free is set by default here.
     * @private
     */
    private _selectedTier: TierModel = { adcs: 6, backups: 2, support: 'basic', tier: 'free' };

    /**
     * Tier options.
     * @private
     */
    private _tiers: any = {};

    get tiers() {
        return this._tiers;
    }

    set resources(resources: any) {
        this._tiers = this.formatResourcesToTiers(resources);
    }

    get selectedTier() {
        return this._selectedTier;
    }

    set selectedTier(tier: any) {
        this._selectedTier = <TierModel>this.tiers[tier];
    }

    /**
     * Page lookup by URL.
     * @param url
     */
    public getPathAndMethodForUrl(url: string): { paths: string | string[], method: string } {
        const urls: any = this.getLookupObject();

        return { paths: urls[url]?.path, method: urls[url]?.method };
    }

    /**
     * Look up object.
     */
    public getLookupObject(): any {
        return {
            '/cm/accounts/recover-keys': { path: '/' },
            '/cm/accounts/settings': { path: '/api/v1/accounts/:id', method: 'GET' },
            '/cm/accounts/sso': { path: '/api/v1/accounts/auth/:provider', method: 'GET' },
            '/cm/accounts/two-factor-auth': { path: '/api/v1/accounts/:id/2fa/setup', method: 'POST' },
            '/cm/adcs/list': { path: '/api/v1/:namespace/adcs/', method: 'GET' },
            '/cm/adcs/list/select-vendor': { path: '/api/v1/:namespace/adcs/', method: 'POST' },
            '/cm/adcs/list/add': { path: '/api/v1/:namespace/adcs/', method: 'POST' },
            '/cm/adcs/list/edit': { path: '/api/v1/:namespace/adcs/:id', method: 'PATCH' },
            '/cm/adcs/shuttle': { path: '/api/v1/:namespace/sidecars/', method: 'GET' },
            '/cm/adcs/shuttle/add': { path: '/api/v1/:namespace/sidecars/:id/adopt', method: 'PATCH' },
            '/cm/adcs/shuttle/edit': { path: '/api/v1/:namespace/sidecars/:id', method: 'PATCH' },
            '/cm/organisation/billing/overview': {
                path: [
                    '/api/v1/billing/accounts',
                    '/api/v1/billing/invoices',
                    '/api/v1/billing/payment-methods',
                    '/api/v1/billing/products'
                ], method: 'GET'
            },
            '/cm/organisation/billing/checkout': {
                path: '/api/v1/billing/payment-methods',
                method: (environment.stripeDisabled ? 'DELETE' : 'POST')
            },
            '/cm/organisation/billing/payment-method': { path: '/api/v1/billing/payment-methods', method: 'POST' },
            '/cm/organisation/namespaces': { path: '/api/v1/organisations/:orgid/namespaces', method: 'GET' },
            '/cm/organisation/namespaces/add': { path: '/api/v1/organisations/:orgid/namespaces', method: 'POST' },
            '/cm/organisation/namespaces/edit': { path: '/api/v1/organisations/:orgid/namespaces/:id', method: 'PATCH' },
            '/cm/organisation/security-group': { path: '/' },
            '/cm/organisation/security-group/auditing': {
                path: ['/api/v1/organisations/:orgid/audit', '/api/v1/organisations/:id/members'],
                method: 'GET'
            },
            '/cm/organisation/security-group/saml': { path: '/api/v1/organisations/:orgid/saml', method: 'POST' },
            '/cm/organisation/settings': { path: '/api/v1/organisations/:id', method: 'POST' },
            '/cm/organisation/users/list': {
                path: [
                    '/api/v1/organisations/:id/members',
                    '/api/v1/roles/',
                    '/api/v1/groups/'
                ], method: 'GET'
            },
            '/cm/organisation/users/user/add': { path: '/api/v1/organisations/:id', method: 'POST' },
            '/cm/organisation/users/user/edit': { path: '/api/v1/organisations/:id/accounts/:memberid', method: 'PATCH' },
            '/cm/organisation/users/user/import': { path: '/api/v1/organisations/:id', method: 'POST' },
            '/cm/organisation/users/role/add': { path: '/api/v1/roles/', method: 'POST' },
            '/cm/organisation/users/role/edit': { path: '/api/v1/roles/:id', method: 'PATCH' },
            '/cm/organisation/users/role/edit/duplicate': { path: '/api/v1/roles/:id', method: 'PATCH' },
            '/cm/organisation/users/group/add': { path: '/api/v1/groups/', method: 'POST' },
            '/cm/organisation/users/group/edit': { path: '/api/v1/groups/:id', method: 'PATCH' },
            '/cm/resources/downloads': { path: '/' },
            '/cm/resources/documentation': { path: '/' },
            '/cm/scheduler/schedules': { path: '/api/v1/:namespace/scheduler/', method: 'GET' },
            '/cm/scheduler/schedules/new': { path: '/api/v1/:namespace/scheduler/', method: 'POST' },
            '/cm/scheduler/schedules/edit': { path: '/api/v1/:namespace/scheduler/:id', method: 'PATCH' }, // Not in use
            '/cm/storage/backups': { path: '/api/v1/:namespace/backups/', method: 'GET' },
            '/cm/security/certificates': { path: '/api/v1/:namespace/adcs/', method: 'GET' },
            '/cm/security/certificates/details': {
                path: '/api/v1/:namespace/security/certificates/:adcid', method: 'GET'
            },
            '/cm/security/insights': { path: '/api/v1/:namespace/security/insights', method: 'GET' },
            '/cm/security/insights/details': { path: '/api/v1/:namespace/security/insights/:adcid', method: 'GET' },
            '/cm/support/tickets': { path: '/api/v1/support/tickets/', method: 'GET' },
            '/cm/support/create-ticket': { path: '/api/v1/support/tickets/', method: 'POST' },
            '/cm/support/conversation': { path: '/api/v1/support/tickets/:id', method: 'PUT' },

            // TODO waiting for backend - pipelines
            // '/cm/scheduler/pipelines/': { path: '/api/v1/:namespace/scheduler/', method: 'PATCH' },
            // '/cm/scheduler/pipelines/new': { path: '/api/v1/:namespace/scheduler/', method: 'PATCH' },
            // '/cm/scheduler/pipelines/edit': { path: '/api/v1/:namespace/scheduler/', method: 'PATCH' },

            // TODO I think this isn't needed as each widget should handle the permissions
            // '/cm/security/dashboard': { path: '/api/v1/:namespace/security/insights', method: 'GET' },

        };
    }

    /**
     * Tier options
     */
    public tierOptions(tier: string) {
        const tiers = {
            free: {
                'dashboard': true,
                'adc_backups': true,
                'online_updates': false,
                'asset_scheduling': false,
                'dedicated_success_manager': false,
                'professional_services': false,
                'secure_direct_access': true,
                'secure_warp_remote_access': false,
                'adc_security_insights': true,
                'adc_certificate_insights': false,
                'single_sign_on': false,
                '2fa': false,
                'audit_log': false,
                // 'license_management': false,
                // 'api_access': false,
                // 'asset_scheduling_pipelines': false,
                // 'white_label': false
            },
            professional: {
                'dashboard': true,
                'adc_backups': true,
                'online_updates': true,
                'asset_scheduling': true,
                'dedicated_success_manager': false,
                'professional_services': false,
                'secure_direct_access': true,
                'secure_warp_remote_access': true,
                'adc_security_insights': true,
                'adc_certificate_insights': true,
                'single_sign_on': false,
                '2fa': false,
                'audit_log': false,
                // 'license_management': true,
                // 'api_access': true,
                // 'asset_scheduling_pipelines': false,
                // 'white_label': false
            },
            business: {
                'dashboard': true,
                'adc_backups': true,
                'online_updates': true,
                'asset_scheduling': true,
                'dedicated_success_manager': false,
                'professional_services': false,
                'secure_direct_access': true,
                'secure_warp_remote_access': true,
                'adc_security_insights': true,
                'adc_certificate_insights': true,
                'single_sign_on': true,
                '2fa': true,
                'audit_log': true,
                // 'license_management': true,
                // 'api_access': true,
                // 'asset_scheduling_pipelines': false,
                // 'white_label': false
            },
            tailored: {
                'dashboard': true,
                'adc_backups': true,
                'online_updates': true,
                'asset_scheduling': true,
                'dedicated_success_manager': true,
                'professional_services': true,
                'secure_direct_access': true,
                'secure_warp_remote_access': true,
                'adc_security_insights': true,
                'adc_certificate_insights': true,
                'single_sign_on': true,
                'single_sign_on_tailored': true,
                '2fa': true,
                'audit_log': true,
                // 'license_management': true,
                // 'api_access': true,
                // 'asset_scheduling_pipelines': true,
                // 'white_label': false
            }
        };

        return tiers[tier];
    }

    /**
     * Format resources to tiers.
     * @param resources
     * @private
     */
    private formatResourcesToTiers(resources: any): any {
        const tiers: any = {};
        for (const resource in resources) {
            if (resources.hasOwnProperty(resource)) {
                const reSrc = resources[resource];
                const apiResources = {
                    backups: reSrc.limits['backups-per-adc'],
                    support: reSrc.values['support-level'],
                    tier: resource,
                    adcs: reSrc.limits['adc-connections']
                };

                tiers[resource] = { ...apiResources, ...this.tierOptions(resource) };
            }
        }

        return tiers;
    }
}
